import type { ProductImage, VariationGroupItem, VariationImage, VariationImagePatch } from '../../../types/product'

// Raw Woo status of a published variation (variation_group payload).
export const PUBLISHED_VARIATION_STATUS = 'publish'

type VariationImageOverrides = Record<string, VariationImagePatch | null>

// A variation's effective image(s) after the editor's unsaved per-variation
// image edits: a `null` override means the image was removed; an object
// override is merged over `item.image`. Without an override, the plural
// `images` list wins and `image` is the fallback. Only images with a `src`
// are returned.
export function effectiveVariationImages(
  item: Pick<VariationGroupItem, '_id' | 'image' | 'images'>,
  overrides?: VariationImageOverrides,
): VariationImage[] {
  const ov = overrides?.[item._id]
  if (ov === null) return []
  if (ov) {
    const merged = { ...(item.image ?? {}), ...ov }
    return merged.src ? [merged] : []
  }
  const fromList = (item.images ?? []).filter((img): img is VariationImage => !!img?.src)
  if (fromList.length > 0) return fromList
  return item.image?.src ? [item.image] : []
}

// An image counted by the SEO checks. Parent images keep their `id`; variation
// images carry the `variationId` (VariationGroupItem._id) they belong to so a
// "Fix" action can open the variation image editor.
export interface SeoImage {
  id?: string
  src?: string
  alt?: string
  variationId?: string
}

// Images the SEO checks score: every parent image, then the images of
// published variations (unsaved edits applied). Variation images whose `src`
// matches a parent image or an earlier variation image are skipped. An empty
// alt is backfilled with `fallbackAlt` (the product name), as the storefront does.
export function collectSeoImages(
  draftImages: Pick<ProductImage, 'id' | 'src' | 'alt'>[],
  variationGroup: Pick<VariationGroupItem, '_id' | 'status' | 'image' | 'images'>[] | undefined,
  overrides?: VariationImageOverrides,
  fallbackAlt?: string,
): SeoImage[] {
  const withAlt = (alt?: string) => alt?.trim() || fallbackAlt?.trim() || alt
  const out: SeoImage[] = draftImages.map((img) => ({ id: img.id, src: img.src, alt: withAlt(img.alt) }))
  const seen = new Set(draftImages.map((img) => img.src).filter((src): src is string => !!src))
  for (const item of variationGroup ?? []) {
    if (item.status !== PUBLISHED_VARIATION_STATUS) continue
    for (const img of effectiveVariationImages(item, overrides)) {
      const src = img.src as string
      if (seen.has(src)) continue
      seen.add(src)
      out.push({ src, alt: withAlt(img.alt), variationId: item._id })
    }
  }
  return out
}
